// src/components/ApiKeySelectorModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Row, Col, message, Spin } from 'antd';
import ProtectedImage from './ProtectedImage';
import { useTheme } from '../App';

// In ApiKeySelectorModal.js, add import at top:
import authAxios from '../services/authService';
const { Option } = Select;

const ApiKeySelectorModal = ({ 
  visible, 
  onSelect, 
  onCancel, 
  orgId, 
  authToken, 
  orgApiKeys,
  globalEnv,
  devIp 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [SelectedModalEnv, setSelectedModalEnv] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Use theme
  const { theme, currentTheme } = useTheme();

  const environments = ['Development', 'Quality Assurance', 'Production'];
  
  // Default background colors for apps without an icon
  const defaultColors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#ff5733', '#33c1ff', '#85ff33'];

  // Compute the organization key for the image (same as for get apps)
  const getOrgKey = () => {
    if (SelectedModalEnv === 'Development') {
      return orgApiKeys?.devAPIKey || '';
    } else if (SelectedModalEnv === 'Quality Assurance') {
      return orgApiKeys?.stagingAPIKey || '';
    } else if (SelectedModalEnv === 'Production') {
      return orgApiKeys?.productionAPIKey || '';
    }
    return '';
  };

  const handleEnvNext = () => {
    if (!SelectedModalEnv) {
      message.error('Please select an environment');
      return;
    }
    setCurrentStep(2);
    fetchApps();
  };

  const fetchApps = async () => {
    setLoading(true);
    try {
      const payload = { organisationId: orgId };
      const response = await authAxios.post('/api/admin/v1/app/get', payload);
      
      if (response.data?.status === 'success') {
        const appsData = response.data.data.projectDetails || [];
        setApps(appsData);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch apps');
      }
    } catch (error) {
      message.error(`Error fetching apps: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAppSelect = (app) => {
    let selectedApiKey = '';
    if (SelectedModalEnv === 'Development') {
      selectedApiKey = app.apiKeys?.clientDevAPIKey?.[0] || '';
    } else if (SelectedModalEnv === 'Quality Assurance') {
      selectedApiKey = app.apiKeys?.clientStagingAPIKey?.[0] || '';
    } else if (SelectedModalEnv === 'Production') {
      selectedApiKey = app.apiKeys?.clientProductionAPIKey?.[0] || '';
    }
    if (!selectedApiKey) {
      message.error('No API key available for this app in the selected environment');
      return;
    }
    onSelect(selectedApiKey);
    setCurrentStep(1);
    setSelectedModalEnv(null);
    setApps([]);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setApps([]);
  };

  useEffect(() => {
    if (!visible) {
      setCurrentStep(1);
      setSelectedModalEnv(null);
      setApps([]);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onCancel={() => {
        onCancel();
        setCurrentStep(1);
        setSelectedModalEnv(null);
        setApps([]);
      }}
      footer={null}
      title={<span style={{ color: theme.text }}>Select Your API Key</span>}
      style={{ top: 20 }}
    >
      {currentStep === 1 && (
        <div>
          <h3 style={{ color: theme.text }}>Select Your Environment</h3>
          <Select
            style={{ width: '100%', marginBottom: 20 }}
            placeholder="Choose an environment"
            onChange={value => setSelectedModalEnv(value)}
            value={SelectedModalEnv}
          >
            {environments.map(env => (
              <Option key={env} value={env}>
                {env}
              </Option>
            ))}
          </Select>
          <Button type="primary" onClick={handleEnvNext} block>
            Next
          </Button>
        </div>
      )}
      {currentStep === 2 && (
        <div>
          <h3 style={{ color: theme.text }}>Select Your App</h3>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin tip="Loading apps..." />
            </div>
          ) : (
            <Row gutter={[32, 32]}>
              {apps.length > 0 ? (
                apps.map((app, index) => (
                  <Col key={app.id} xs={24} sm={12} md={6} lg={6} style={{ textAlign: 'center' }}>
                    <div 
                      onClick={() => handleAppSelect(app)} 
                      style={{ 
                        cursor: 'pointer', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        padding: '12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.surfaceLight;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {app.iconUrl ? (
                        <ProtectedImage
                          src={app.iconUrl}
                          apiKey={getOrgKey()}
                          alt={app.name}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: '20%',
                            boxShadow: currentTheme === 'dark' 
                              ? '0 4px 12px rgba(0, 0, 0, 0.4)' 
                              : '0 4px 12px rgba(0, 0, 0, 0.1)',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: '20%',
                            backgroundColor: defaultColors[index % defaultColors.length],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 18,
                            color: 'white',
                            fontWeight: 'bold',
                            boxShadow: currentTheme === 'dark' 
                              ? '0 4px 12px rgba(0, 0, 0, 0.4)' 
                              : '0 4px 12px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          {app.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ 
                        marginTop: 8, 
                        fontSize: '13px',
                        color: theme.text,
                        fontWeight: '500'
                      }}>
                        {app.name}
                      </div>
                    </div>
                  </Col>
                ))
              ) : (
                <Col span={24}>
                  <p style={{ textAlign: 'center', color: theme.textSecondary }}>
                    No apps available for the selected environment.
                  </p>
                </Col>
              )}
            </Row>
          )}
          <Button style={{ marginTop: 20 }} onClick={handleBack}>
            Back
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ApiKeySelectorModal;